import React, { useContext, useEffect, useState } from "react";
import PagePreview from "./Preview";
import { useSelector } from "react-redux";
import { getData } from "~/functions/gridFunctions";
import PageContainer from "~/components/PageContainer";
import { FullGridContext } from "../..";
import { format, parseISO } from "date-fns";
import {
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { AlertContext, PageContext } from "~/App";
import Settlement from "./Settlement";
import { receivableStatusesOptions } from "~/functions/selectPopoverOptions";
import api, { baseURL } from "~/services/api";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";
import { Pin } from "lucide-react";
import ModalFilters, { getStoredFilters } from "./ModalFilters";

const USDollar = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  currencySign: "accounting",
});

export function FinancialOutbounds() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { pages } = useContext(PageContext);
  const paths = pathname.split("/");
  const redirectRouteName = pages
    .find((page) => page.name === paths[1])
    .children.find((page) => page.path === `/${paths[1]}/${paths[2]}`)
    .children[0].path;
  useEffect(() => {
    if (pathname.toUpperCase() === `/${paths[1]}/${paths[2]}`.toUpperCase()) {
      navigate(`${redirectRouteName}`);
    }
  }, [pathname]);
  return <Outlet />;
}

export default function FinancialPayees() {
  const filial = useSelector((state) => state.auth.filial);
  const defaultOrderBy = { column: "due_date", asc: true };
  const defaultGridHeader = [
    {
      title: "Issuer Name",
      name: ["issuer", "name"],
      type: "text",
      filter: false,
    },
    {
      title: "Invoice Number",
      name: "invoice_number",
      type: "text",
      filter: false,
    },
    {
      title: "Due Date",
      name: "due_date",
      type: "date",
      filter: false,
    },
    {
      title: "Amount",
      name: "amount",
      type: "currency",
      filter: false,
    },
    // {
    //   title: "Discount",
    //   name: "discount",
    //   type: "currency",
    //   filter: false,
    // },
    // {
    //   title: "Fee",
    //   name: "fee",
    //   type: "currency",
    //   filter: false,
    // },
    // {
    //   title: "Total",
    //   name: "total",
    //   type: "currency",
    //   filter: false,
    // },
    {
      title: "Balance",
      name: "balance",
      type: "currency",
      filter: false,
    },
    {
      title: "Memo",
      name: "memo",
      type: "text",
      filter: false,
    },
    {
      title: "Chart of Account",
      name: "chartOfAccount",
      type: "text",
      filter: false,
    },
    {
      title: "Status",
      name: "status",
      type: "text",
      filter: false,
    },
  ];
  const storedFilters = getStoredFilters();
  const [settlementOpen, setSettlementOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [applyingFilters, setApplyingFilters] = useState(false);
  const [summary, setSummary] = useState(null);
  const [filtersParams, setFiltersParams] = useState(() => {
    if (!storedFilters?.filters) return null;
    const hasValues = Object.values(storedFilters.filters).some(
      (val) => val && val !== "null"
    );
    return hasValues ? storedFilters.filters : null;
  });
  const [filtersChartCodes, setFiltersChartCodes] = useState(
    () => storedFilters?.chartCodes || []
  );
  const [filtersPinned, setFiltersPinned] = useState(
    () => localStorage.getItem("payees_filters_pinned") === "true"
  );
  const { alertBox } = useContext(AlertContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    accessModule,
    activeFilters,
    gridData,
    gridDetails,
    gridHeader,
    handleFilters,
    handleOpened,
    limit,
    loadingData,
    opened,
    orderBy,
    page,
    pages,
    search,
    setActiveFilters,
    setGridData,
    setGridDetails,
    setGridHeader,
    setLimit,
    setLoadingData,
    setOpened,
    setOrderBy,
    setPage,
    setPages,
    setSearch,
    setTotalRows,
    setSuccessfullyUpdated,
    successfullyUpdated,
    totalRows,
  } = useContext(FullGridContext);

  const pageAccess = accessModule.children.find(
    (el) => el.alias === "financial-payees"
  );

  function handleSettlement() {
    const newVarOpened = !settlementOpen;
    setSettlementOpen(newVarOpened);
    if (!newVarOpened) {
      loader();
    }
  }

  useEffect(() => {
    setActiveFilters([]);
  }, []);

  async function loader() {
    setLoadingData(true);
    if (filtersParams) {
      const params = new URLSearchParams();
      params.set("limit", limit);
      params.set("page", page);
      params.set(
        "orderBy",
        orderBy?.column ? orderBy.column : defaultOrderBy.column
      );
      params.set(
        "orderASC",
        orderBy ? (orderBy.asc ? "ASC" : "DESC") : defaultOrderBy.asc ? "ASC" : "DESC"
      );
      params.set("search", search?.value || search || "");

      Object.entries(filtersParams).forEach(([key, value]) => {
        params.set(key, value || "null");
      });

      try {
        const hasChartCodes =
          filtersChartCodes && filtersChartCodes.length > 0;
        const endpoint = hasChartCodes ? "/payee/filter" : "/payee";
        const requestPromise = hasChartCodes
          ? api.post(`${endpoint}?${params.toString()}`, {
              chart_codes: filtersChartCodes,
            })
          : api.get(`${endpoint}?${params.toString()}`);

        const { data } = await requestPromise;

        const pagesCalc = Math.ceil((data?.totalRows || 0) / limit) || 1;
        setPages(pagesCalc);
        setGridDetails({
          totalRows: data?.totalRows || 0,
          pages: pagesCalc,
        });

        const gridDataValues = (data?.rows || []).map(
          (
            {
              id,
              canceled_at,
              issuer,
              invoice_number,
              due_date,
              amount,
              balance,
              memo,
              status,
              chartOfAccount,
            },
            index
          ) => {
            let chartOfAccountName = "";
            if (chartOfAccount) {
              chartOfAccountName = chartOfAccount.name;
            }
            return {
              show: true,
              id,
              fields: [
                issuer.name,
                invoice_number ? invoice_number.toString() : null,
                format(parseISO(due_date), "yyyy-MM-dd"),
                "$ " + amount.toFixed(2),
                "$ " + balance.toFixed(2),
                memo,
                chartOfAccountName,
                status,
              ],
              selectable: status !== "Paid",
              canceled: canceled_at,
              page: Math.ceil((index + 1) / limit),
            };
          }
        );

        setGridData(gridDataValues);
        setSummary(data?.summary || null);
      } catch (err) {
        console.log("Error loading filtered payees:", err);
      } finally {
        setLoadingData(false);
      }
      return;
    }

    const data = await getData("payee", {
      limit,
      page,
      orderBy,
      setPages,
      setGridData,
      search,
      defaultGridHeader,
      defaultOrderBy,
      setGridDetails,
    });
    if (!data) {
      return;
    }
    const gridDataValues = data.map(
      (
        {
          id,
          canceled_at,
          filial,
          issuer,
          invoice_number,
          due_date,
          amount,
          discount,
          fee,
          total,
          balance,
          memo,
          status,
          chartOfAccount,
        },
        index
      ) => {
        let chartOfAccountName = "";
        if (chartOfAccount) {
          chartOfAccountName = chartOfAccount.name;
        }
        const ret = {
          show: true,
          id,
          fields: [
            issuer.name,
            invoice_number ? invoice_number.toString() : null,
            format(parseISO(due_date), "yyyy-MM-dd"),
            "$ " + amount.toFixed(2),
            // "$ " + discount.toFixed(2),
            // "$ " + fee.toFixed(2),
            // "$ " + total.toFixed(2),
            "$ " + balance.toFixed(2),
            memo,
            chartOfAccountName,
            status,
          ],
          selectable: status !== "Paid",
          canceled: canceled_at,
          page: Math.ceil((index + 1) / limit),
        };
        return ret;
      }
    );
    setGridData(gridDataValues);
    setSummary(null);
    setLoadingData(false);
  }

  useEffect(() => {
    if (!settlementOpen) {
      setSelected([]);
    }
    loader();
    if (searchParams?.has("id")) {
      const targetId = searchParams.get("id");
      setSearchParams("");
      setOpened(targetId);
    }
  }, [opened, filial, orderBy, search, limit, page, filtersParams, filtersChartCodes]);

  async function handleDelete() {
    if (selected.length === 0) {
      return;
    }
    alertBox({
      title: "Attention!",
      descriptionHTML:
        "Are you sure you want to delete the selected payee? This action cannot be undone.",
      buttons: [
        {
          title: "No",
          class: "cancel",
        },
        {
          title: "Yes",
          onPress: async () => {
            const promises = [];
            let hasChanged = false;
            selected.map((payee) => {
              promises.push(
                api
                  .delete(`/payee/${payee.id}`)
                  .then((response) => {
                    toast(response.data.message, { autoClose: 1000 });
                  })
                  .catch((err) => {
                    toast(err.response.data.error, {
                      type: "error",
                      autoClose: 3000,
                    });
                  })
              );
            });
            Promise.all(promises).then(() => {
              setSelected([]);
              loader();
            });
          },
        },
      ],
    });
  }

  const applyFiltersToGrid = async (queryString, chartCodes = []) => {
    setApplyingFilters(true);
    setLoadingData(true);

    try {
      const hasChartCodes = chartCodes && chartCodes.length > 0;

      const requestPromise = hasChartCodes
        ? api.post(queryString, { chart_codes: chartCodes })
        : api.get(queryString);

      const { data } = await requestPromise;

      const pagesCalc = Math.ceil((data?.totalRows || 0) / limit) || 1;
      setPages(pagesCalc);
      setGridDetails({
        totalRows: data?.totalRows || 0,
        pages: pagesCalc,
      });

      const gridDataValues = data.rows.map(
        (
          {
            id,
            canceled_at,
            filial,
            issuer,
            invoice_number,
            due_date,
            amount,
            balance,
            memo,
            status,
            chartOfAccount,
          },
          index
        ) => {
          let chartOfAccountName = "";
          if (chartOfAccount) {
            chartOfAccountName = chartOfAccount.name;
          }
          return {
            show: true,
            id,
            fields: [
              issuer.name,
              invoice_number ? invoice_number.toString() : null,
              format(parseISO(due_date), "yyyy-MM-dd"),
              "$ " + amount.toFixed(2),
              "$ " + balance.toFixed(2),
              memo,
              chartOfAccountName,
              status,
            ],
            selectable: status !== "Paid",
            canceled: canceled_at,
            page: Math.ceil((index + 1) / limit),
          };
        }
      );

      setGridData(gridDataValues);
      setSummary(data?.summary || null);
      const parsed = new URLSearchParams(queryString.split("?")[1] || "");
      const newFilters = {};
      parsed.forEach((value, key) => {
        if (!["limit", "page", "orderBy", "orderASC", "search"].includes(key)) {
          newFilters[key] = value;
        }
      });
      const hasValues = Object.values(newFilters).some(
        (val) => val && val !== "null"
      );
      setFiltersParams(hasValues ? newFilters : null);
      setFiltersChartCodes(hasChartCodes ? chartCodes : []);
      setPage(1);
    } catch (err) {
      console.log("Error applying filters:", err);
      toast(err?.response?.data?.error || "Failed to apply filters", {
        type: "error",
        autoClose: 3000,
      });
    } finally {
      setApplyingFilters(false);
      setLoadingData(false);
      setFiltersModalOpen(false);
    }
  };

  return (
    <>
      <ModalFilters
        opened={filtersModalOpen}
        applying={applyingFilters}
        onClose={() => {
          setFiltersModalOpen(false);
          setFiltersPinned(
            localStorage.getItem("payees_filters_pinned") === "true"
          );
        }}
        onApplyFilters={applyFiltersToGrid}
      />
      <PageContainer
        FullGridContext={FullGridContext}
        PagePreview={PagePreview}
        pageAccess={pageAccess}
        defaultGridHeader={defaultGridHeader}
        summaryRow={summary ? {
          fields: [
            `Summary (${summary.count})`, "", "",
            USDollar.format(Number(summary.amount || 0)),
            USDollar.format(Number(summary.balance || 0)),
            "", "", "",
          ],
        } : null}
        selection={{
          multiple: false,
          selected,
          setSelected,
          functions: [
            {
              title: "Settlement",
              fun: handleSettlement,
              icon: "ReplaceAll",
              Page: Settlement,
              opened: settlementOpen,
              setOpened: setSettlementOpen,
              selected,
            },
            {
              title: "Delete",
              fun: handleDelete,
              icon: "X",
              Page: null,
              opened: false,
              setOpened: () => null,
              selected,
            },
          ],
        }}
        filtersModal={{
          onOpen: () => setFiltersModalOpen(true),
          viewSelect: filtersPinned ? (
            <div className="flex items-center gap-1 text-orange-500 text-xs font-medium">
              <Pin size={13} className="fill-orange-500" />
              Filters Applied
            </div>
          ) : null,
        }}
      />
    </>
  );
}
